import appConfig from "constants/appConfig";
import { checkIfUseCloudFlare } from "tmp/canaryRelease/cloudflareVOD";
import { getEventIdFromHref } from "utility";
import { fetchFbTokenWithoutTokenRefresh, fetchUserId } from "utility/firebase";

import { setUseTHEOplayerV2ToLocalStorage } from "./enableTheoPlayerV2";

import type { StreamingType } from "@spwn/types";

export const attachInterceptorToTHEOplayer = ({
  player,
  streamingType,
  videoId,
  isLLHLSUsed,
}: {
  player: THEOplayer.Player;
  streamingType: StreamingType;
  videoId: string;
  isLLHLSUsed: boolean;
}) => {
  let playerErrorHappened = false;

  const playerErrorHandler = (errorData: unknown) => {
    try {
      void (async () => {
        checkIfShouldSetUseTHEOplayerV2(errorData);
        if (checkIfUseCloudFlare({ streamingType, videoId })) {
          playerErrorHappened = true;
          localStorage.setItem("playerErrorHappened_v1", "true");
        }

        const object = { expiredAt: new Date().getTime() + 1800 * 1000 };
        localStorage.setItem("useCloudfront", JSON.stringify(object));

        const fbToken = await fetchFbTokenWithoutTokenRefresh();
        const userId = fetchUserId();

        if (appConfig.backendApiUrl && fbToken) {
          const url = `${appConfig.backendApiUrl}/portal/python-functions/playerErrorLogging`;

          const eventId = getEventIdFromHref(
            window.location.pathname,
            "events"
          );
          const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

          const { userAgent, language } = window.navigator;

          const commitHash = appConfig.gitCommitHash;

          await fetch(url, {
            mode: "cors",
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${fbToken}`,
            },
            body: JSON.stringify({
              error: errorData,
              eventId,
              userAgent,
              userId,
              language,
              timezone,
              commitHash,
              isLLHLSUsed,
            }),
            credentials: "include",
          });
        }
      })();
    } catch (e) {
      console.error(e);
    }
  };

  const httpErrorInterceptor = (response: {
    status: number;
    request: { type: string; url: string };
  }) => {
    // status 0 はネットワークエラー、CORS違反、タイムアウトなど
    // status 4xx-5xx は通常のHTTPエラー
    if (
      response.status === 0 ||
      (response.status >= 400 && response.status <= 599)
    ) {
      const errorDetails = {
        status: response.status,
        fileType: response.request.type,
        url: response.request.url,
      };

      playerErrorHandler(errorDetails);
    }
  };

  const checkIfShouldSetUseTHEOplayerV2 = (errorData: unknown) => {
    try {
      if (
        errorData !== null &&
        typeof errorData === "object" &&
        "errorObject" in errorData &&
        typeof errorData.errorObject === "object" &&
        errorData.errorObject !== null &&
        "message" in errorData.errorObject
      ) {
        // theoplayerをv8にupgradeした際に、新たに出現したエラー
        // ブラウザ拡張機能が原因で発生している可能性が高い
        const THEOPLAYER_V8_ERROR =
          "THEOplayer workers could not be loaded. Please check the configuration of the libraryLocation.";

        /**
         * ストファイで報告があったエラー。上記のTHEOPLAYER_V8_ERRORと同様に、ブラウザ拡張機能が原因で発生している可能性が高い。
         */
        const THEOPLAYER_V8_UNKNOWN_REQUEST_ERROR =
          "Unknown request type: undefined";

        if (
          errorData.errorObject.message === THEOPLAYER_V8_ERROR ||
          errorData.errorObject.message === THEOPLAYER_V8_UNKNOWN_REQUEST_ERROR
        ) {
          setUseTHEOplayerV2ToLocalStorage();
          window.location.reload();
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const playerLogHandler = (logData: unknown) => {
    try {
      void (async () => {
        console.log(logData);

        const playerErrorHappened_v1 = localStorage.getItem(
          "playerErrorHappened_v1"
        );

        if (!(playerErrorHappened_v1 === "true" || playerErrorHappened)) {
          return;
        }

        const fbToken = await fetchFbTokenWithoutTokenRefresh();

        if (appConfig.backendApiUrl && fbToken) {
          const userId = fetchUserId();
          const url = `${appConfig.backendApiUrl}/portal/python-functions/playerLogging`;

          const eventId = getEventIdFromHref(
            window.location.pathname,
            "events"
          );
          const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

          const { userAgent, language } = window.navigator;

          const commitHash = appConfig.gitCommitHash;

          await fetch(url, {
            mode: "cors",
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${fbToken}`,
            },
            body: JSON.stringify({
              userLog: logData,
              eventId,
              userAgent,
              userId,
              language,
              timezone,
              commitHash,
              isLLHLSUsed,
            }),
            credentials: "include",
          });
        }
      })();
    } catch (e) {
      console.error(e);
    }
  };

  player.network.addResponseInterceptor(httpErrorInterceptor);

  player.addEventListener("error", playerErrorHandler);

  player.addEventListener(
    [
      "play",
      "playing",
      "durationchange",
      "ended",
      "seeking",
      "seeked",
      "waiting",
      "pause",
      "volumechange",
    ] as const,
    playerLogHandler
  );
};
